import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Field } from './styles';
import {
  Block,
  Button,
  DetailWrap,
  ErrorText,
  Muted,
  ReasonLine,
} from './tableStyles';
import { reasonMeaning } from './reasonMeanings';
import {
  channelLabel,
  friendlyLifecycleError,
  reasonLabel,
} from '../shared/labels';

import ROUTES from '@/routes/routes';
import {
  JourneyChanges,
  JourneyStatsWindow,
  LifecycleJourney,
} from '@/services/lifecycleAPI';

// The same words graphy's rule reads in (friendlyLifecycleError), not the
// API field names (review D-04).
const HOLDOUT_ERROR = friendlyLifecycleError('holdout_percent must be 0 to 50');
const THRESHOLD_ERROR = friendlyLifecycleError(
  'high_demand_threshold must be a number',
);
const CHANNEL_ORDER = ['sms', 'push', 'email'];

type Props = {
  journey: LifecycleJourney;
  stats: JourneyStatsWindow;
  windowLabel: string;
  canEdit: boolean;
  pending: boolean;
  error: string | null;
  onSave: (changes: JourneyChanges) => Promise<boolean>;
  onCancel: () => void;
};

const sentLine = (sent: Record<string, number>) => {
  const keys = [
    ...CHANNEL_ORDER.filter((key) => sent[key]),
    ...Object.keys(sent).filter((key) => !CHANNEL_ORDER.includes(key)),
  ];
  return keys.map((key) => `${channelLabel(key)} ${sent[key]}`).join(' · ');
};

const JourneyDetail = ({
  journey,
  stats,
  windowLabel,
  canEdit,
  pending,
  error,
  onSave,
  onCancel,
}: Props) => {
  const key = journey.journey_key;
  const savedThreshold =
    journey.high_demand_threshold === null
      ? ''
      : String(journey.high_demand_threshold);
  const [holdout, setHoldout] = useState(String(journey.holdout_percent));
  const [threshold, setThreshold] = useState(savedThreshold);
  const [holdoutError, setHoldoutError] = useState(false);
  const [thresholdError, setThresholdError] = useState(false);
  const tip = canEdit ? undefined : 'Tenant admin only';
  const hasThreshold = key === 'J2';

  useEffect(() => {
    setHoldout(String(journey.holdout_percent));
    setThreshold(savedThreshold);
  }, [journey.holdout_percent, savedThreshold]);

  const save = async () => {
    const changes: JourneyChanges = {};
    const holdoutText = holdout.trim();
    const badHoldout = !/^\d+$/.test(holdoutText) || Number(holdoutText) > 50;
    const thresholdText = threshold.trim();
    const badThreshold = thresholdText !== '' && !/^\d+$/.test(thresholdText);
    setHoldoutError(badHoldout);
    setThresholdError(hasThreshold && badThreshold);
    if (badHoldout || (hasThreshold && badThreshold)) return;
    if (Number(holdoutText) !== journey.holdout_percent) {
      changes.holdout_percent = Number(holdoutText);
    }
    if (hasThreshold && thresholdText !== savedThreshold) {
      changes.high_demand_threshold =
        thresholdText === '' ? null : Number(thresholdText);
    }
    if (Object.keys(changes).length === 0) return;
    await onSave(changes);
  };

  const skipped = Object.entries(stats.skipped).sort((a, b) => b[1] - a[1]);
  const sends = sentLine(stats.messages_sent);
  const copySteps = journey.definition.steps.filter(
    (step) => step.kind !== 'call_task',
  );

  return (
    <DetailWrap data-testid={`journeyDetail-${key}`}>
      <Block>
        <h3>Settings</h3>
        <Field>
          <label htmlFor={`holdout-${key}`}>Holdout %</label>
          <input
            aria-describedby={`holdoutHelp-${key}`}
            disabled={!canEdit || pending}
            id={`holdout-${key}`}
            inputMode="numeric"
            style={{ width: 120 }}
            title={tip}
            value={holdout}
            onChange={(e) => setHoldout(e.target.value)}
          />
          <Muted id={`holdoutHelp-${key}`}>
            Workers in holdout get no messages, so we can measure the journey. 0
            to 50.
          </Muted>
          {holdoutError && <ErrorText role="alert">{HOLDOUT_ERROR}</ErrorText>}
        </Field>
        {hasThreshold && (
          <Field>
            <label htmlFor={`threshold-${key}`}>High-demand threshold</label>
            <input
              disabled={!canEdit || pending}
              id={`threshold-${key}`}
              inputMode="numeric"
              style={{ width: 120 }}
              title={tip}
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
            />
            <Muted>
              Call task only when the worker's zip has more open shifts than
              this. Empty = off.
            </Muted>
            {thresholdError && (
              <ErrorText role="alert">{THRESHOLD_ERROR}</ErrorText>
            )}
          </Field>
        )}
        {error && <ErrorText role="alert">{error}</ErrorText>}
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            data-testid={`journeySaveBtn-${key}`}
            disabled={!canEdit || pending}
            primary
            title={tip}
            type="button"
            onClick={save}
          >
            Save
          </Button>
          <Button type="button" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </Block>
      <Block>
        <h3>
          Messages sent <Muted>({windowLabel})</Muted>
        </h3>
        {sends ? (
          <>
            <div>{sends}</div>
            <Muted>
              Delivered {stats.delivered} · Clicked {stats.clicked} · Failed{' '}
              {stats.failed}
            </Muted>
          </>
        ) : (
          <Muted>Nothing sent in this window.</Muted>
        )}
        {skipped.length > 0 && (
          <>
            <strong>Skipped by reason</strong>
            {skipped.map(([code, count]) => {
              const { label, known } = reasonLabel(code);
              return (
                <ReasonLine key={code} title={reasonMeaning(code, label)}>
                  <span>
                    {label}
                    {known && <Muted> ({code})</Muted>}
                  </span>
                  <strong>{count}</strong>
                </ReasonLine>
              );
            })}
          </>
        )}
      </Block>
      <Block>
        <h3>
          Exits <Muted>({windowLabel})</Muted>
        </h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: '4px 12px',
          }}
        >
          <span>Goal reached</span>
          <strong>{stats.exited_goal}</strong>
          <span>Window ended</span>
          <strong>{stats.exited_window}</strong>
          <span>Another journey took over</span>
          <strong>{stats.exited_superseded}</strong>
          <span>Cannot contact</span>
          <strong>{stats.exited_suppressed}</strong>
        </div>
        <strong>Edit copy</strong>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {copySteps.map((step) => (
            <Link
              key={step.key}
              style={{ fontSize: 13, color: '#1E8E3E' }}
              to={`/${ROUTES.lifecycle}?tab=copy&message=${step.key}`}
            >
              {step.label} <Muted>({step.key})</Muted>
            </Link>
          ))}
        </div>
      </Block>
    </DetailWrap>
  );
};

export default JourneyDetail;
